import menuConfig from '../config/addContentMenu.json';
import templateConfig from '../config/addContentTemplates.json';
import {
  isVoidElement,
  SAFE_TAGS,
} from './htmlEditorUtils.js';
import { createTemplateNodeFromDefinition } from './htmlElementTemplates.js';
import { EDITOR_MODES } from './simpleEditorMode.js';

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const supportsMode = (entry, mode) => (
  Array.isArray(entry?.modes) && entry.modes.includes(mode)
);

const findNamedTemplate = (type, key) => (
  templateConfig.templates?.[type]?.[key] || null
);

const validateTemplateNode = (definition, path, errors) => {
  if (typeof definition === 'string' || typeof definition === 'number') return;
  if (!definition || typeof definition !== 'object' || Array.isArray(definition)) {
    errors.push(`${path} contains an invalid node.`);
    return;
  }
  if (definition.type === 'text') {
    if (!Object.prototype.hasOwnProperty.call(definition, 'content')) {
      errors.push(`${path} contains a text node without content.`);
    }
    return;
  }
  if (!SAFE_TAGS.has(definition.type)) {
    errors.push(`${path} contains an unsafe HTML tag.`);
  }
  if (definition.children !== undefined && !Array.isArray(definition.children)) {
    errors.push(`${path} children must be an array.`);
    return;
  }
  if (isVoidElement(definition.type) && definition.children?.length) {
    errors.push(`${path} gives children to a void element.`);
  }

  const attributes = definition.attributes || {};
  if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) {
    errors.push(`${path} attributes must be an object.`);
  } else {
    Object.entries(attributes).forEach(([name, value]) => {
      const lowerName = name.toLowerCase();
      if (UNSAFE_KEYS.has(name) || lowerName.startsWith('on')) {
        errors.push(`${path} contains an unsafe attribute.`);
      }
      if ((lowerName === 'href' || lowerName === 'src')
        && /^\s*javascript:/i.test(String(value || ''))) {
        errors.push(`${path} contains an unsafe URL.`);
      }
      if (lowerName === 'class' && !Array.isArray(value)) {
        errors.push(`${path} classes must be an array.`);
      }
    });
  }

  (definition.children || []).forEach((child, index) => {
    validateTemplateNode(child, `${path}.children[${index}]`, errors);
  });
};

export const getAddContentCategories = (mode) => (
  menuConfig.categories.filter((category) => supportsMode(category, mode))
);

export const getConfiguredAddContentItems = (
  mode,
  { isTagAllowed = () => true } = {}
) => (
  menuConfig.items.filter((item) => {
    if (!supportsMode(item, mode)) return false;
    if (item.template?.type !== 'html-tag') return true;
    return isTagAllowed(item.template.key);
  })
);

export const createConfiguredAddContentNode = (item) => {
  const { type, key } = item?.template || {};
  const definition = findNamedTemplate(type, key);
  if (!definition) {
    throw new Error(`Unknown Add Content template: ${type || 'missing'} / ${key || 'missing'}`);
  }
  return createTemplateNodeFromDefinition(definition);
};

export const validateAddContentConfig = () => {
  const errors = [];
  const categoryIds = new Set();
  const categoriesById = new Map();
  const itemIds = new Set();
  const validModes = new Set(Object.values(EDITOR_MODES));

  menuConfig.categories.forEach((category) => {
    if (!category.id || categoryIds.has(category.id)) {
      errors.push(`Duplicate or missing category ID: ${category.id || 'missing'}`);
    }
    categoryIds.add(category.id);
    categoriesById.set(category.id, category);
    if (!category.label || !category.iconClass) {
      errors.push(`Category ${category.id || 'missing'} needs a label and icon.`);
    }
    if (!category.modes?.every((mode) => validModes.has(mode))) {
      errors.push(`Category ${category.id || 'missing'} has an invalid mode.`);
    }
  });

  menuConfig.items.forEach((item) => {
    if (!item.id || itemIds.has(item.id)) {
      errors.push(`Duplicate or missing item ID: ${item.id || 'missing'}`);
    }
    itemIds.add(item.id);
    if (!categoryIds.has(item.category)) {
      errors.push(`Item ${item.id || 'missing'} has an unknown category.`);
    }
    if (!item.label || !item.detail || !item.iconClass) {
      errors.push(`Item ${item.id || 'missing'} needs a label, detail, and icon.`);
    }
    if (!item.modes?.every((mode) => validModes.has(mode))) {
      errors.push(`Item ${item.id || 'missing'} has an invalid mode.`);
    }
    const category = categoriesById.get(item.category);
    if (category && !item.modes?.every((mode) => category.modes.includes(mode))) {
      errors.push(`Item ${item.id || 'missing'} is visible outside its category modes.`);
    }
    if (item.group && !category?.groups?.some((group) => group.id === item.group)) {
      errors.push(`Item ${item.id || 'missing'} has an unknown category group.`);
    }
    if (!findNamedTemplate(item.template?.type, item.template?.key)) {
      errors.push(`Item ${item.id || 'missing'} has an unknown template.`);
    }
  });

  Object.entries(templateConfig.templates || {}).forEach(([type, templates]) => {
    Object.entries(templates || {}).forEach(([key, definition]) => {
      validateTemplateNode(definition, `${type}.${key}`, errors);
    });
  });

  return errors;
};

export { menuConfig as ADD_CONTENT_MENU_CONFIG };
export { templateConfig as ADD_CONTENT_TEMPLATE_CONFIG };
